import { API_BASE_URL } from '../config/apiConfig';

const DOCUMENT_API_URL = `${API_BASE_URL}/employee-documents`;

export interface EmployeeDocumentItem {
  id: number;
  documentType: string;
  documentTypeLabel: string;
  personName: string;
  eraYear: number;
  revisionMonth: number;
  originalFileName: string;
}

export interface DocumentUploadResult {
  success: boolean;
  message: string;
  replaced?: boolean;
  data?: EmployeeDocumentItem;
}

export interface DocumentListResult {
  success: boolean;
  message: string;
  data: EmployeeDocumentItem[];
}

function authHeaders(): HeadersInit {
  const token = sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function readJson(response: Response): Promise<Record<string, unknown> | null> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function failureMessage(response: Response, body: Record<string, unknown> | null, fallback: string): string {
  if (response.status === 401) return 'ログインが必要です。';
  const message = body && typeof body.message === 'string' ? body.message : '';
  return message || fallback;
}

export async function uploadEmployeeDocument(input: {
  file: File;
  documentType: string;
  personName: string;
  eraYear: number;
  revisionMonth: number;
}): Promise<DocumentUploadResult> {
  const body = new FormData();
  body.append('file', input.file);
  body.append('documentType', input.documentType);
  body.append('personName', input.personName);
  body.append('eraYear', String(input.eraYear));
  body.append('revisionMonth', String(input.revisionMonth));
  try {
    const response = await fetch(DOCUMENT_API_URL, {
      method: 'POST',
      headers: authHeaders(),
      body,
    });
    const json = await readJson(response);
    if (!response.ok || json?.success === false) {
      return {
        success: false,
        message: failureMessage(response, json, '書類の保存に失敗しました。'),
      };
    }
    return {
      success: true,
      message: typeof json?.message === 'string' ? json.message : '書類を保存しました。',
      replaced: json?.replaced === true,
      data: json?.data as EmployeeDocumentItem | undefined,
    };
  } catch {
    return { success: false, message: '書類の保存に失敗しました。' };
  }
}

export async function listMyDocuments(): Promise<DocumentListResult> {
  try {
    const response = await fetch(`${DOCUMENT_API_URL}/mine`, { headers: authHeaders() });
    const json = await readJson(response);
    if (!response.ok || json?.success === false) {
      return {
        success: false,
        message: failureMessage(response, json, '書類を読み込めませんでした。'),
        data: [],
      };
    }
    const rawData = json?.data;
    const data = Array.isArray(rawData) ? rawData as EmployeeDocumentItem[] : [];
    return { success: true, message: '', data };
  } catch {
    return { success: false, message: '書類を読み込めませんでした。', data: [] };
  }
}

function pdfFileName(fileName: string): string {
  const base = fileName.trim().split(/[/\\]/).pop() || '';
  if (!base) return '書類.pdf';
  return base.toLowerCase().endsWith('.pdf') ? base : `${base}.pdf`;
}

async function fetchPdf(
  url: string,
  fallback: string,
): Promise<{ blob: Blob } | { message: string }> {
  try {
    const response = await fetch(url, { headers: authHeaders() });
    const contentType = response.headers.get('content-type') || '';
    if (!response.ok || contentType.includes('application/json')) {
      const json = await readJson(response);
      return { message: failureMessage(response, json, fallback) };
    }
    return { blob: await response.blob() };
  } catch {
    return { message: fallback };
  }
}

async function openPdf(url: string): Promise<{ success: boolean; message: string }> {
  const result = await fetchPdf(url, '書類を開けませんでした。');
  if (!('blob' in result)) return { success: false, message: result.message };
  const objectUrl = URL.createObjectURL(result.blob);
  const opened = window.open(objectUrl, '_blank', 'noopener');
  if (!opened) {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.target = '_blank';
    link.rel = 'noopener';
    link.click();
  }
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  return { success: true, message: '' };
}

async function downloadPdf(url: string, fileName: string): Promise<{ success: boolean; message: string }> {
  const result = await fetchPdf(url, '書類をダウンロードできませんでした。');
  if (!('blob' in result)) return { success: false, message: result.message };
  const objectUrl = URL.createObjectURL(result.blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = pdfFileName(fileName);
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  return { success: true, message: '' };
}

export function openMyDocument(id: number): Promise<{ success: boolean; message: string }> {
  return openPdf(`${DOCUMENT_API_URL}/mine/${id}/file`);
}

export function downloadMyDocument(id: number, fileName: string): Promise<{ success: boolean; message: string }> {
  return downloadPdf(`${DOCUMENT_API_URL}/mine/${id}/file`, fileName);
}

export async function listEmployeeDocuments(): Promise<DocumentListResult> {
  try {
    const response = await fetch(DOCUMENT_API_URL, { headers: authHeaders() });
    const json = await readJson(response);
    if (!response.ok || json?.success === false) {
      return {
        success: false,
        message: failureMessage(response, json, '書類を読み込めませんでした。'),
        data: [],
      };
    }
    const rawData = json?.data;
    const data = Array.isArray(rawData) ? rawData as EmployeeDocumentItem[] : [];
    return { success: true, message: '', data };
  } catch {
    return { success: false, message: '書類を読み込めませんでした。', data: [] };
  }
}

export function openEmployeeDocument(id: number): Promise<{ success: boolean; message: string }> {
  return openPdf(`${DOCUMENT_API_URL}/${id}/file`);
}

export function downloadEmployeeDocument(id: number, fileName: string): Promise<{ success: boolean; message: string }> {
  return downloadPdf(`${DOCUMENT_API_URL}/${id}/file`, fileName);
}

export async function deleteEmployeeDocument(id: number): Promise<{ success: boolean; message: string }> {
  try {
    const response = await fetch(`${DOCUMENT_API_URL}/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    const json = await readJson(response);
    if (!response.ok || json?.success === false) {
      return {
        success: false,
        message: failureMessage(response, json, '書類を削除できませんでした。'),
      };
    }
    return {
      success: true,
      message: typeof json?.message === 'string' ? json.message : '書類を削除しました。',
    };
  } catch {
    return { success: false, message: '書類を削除できませんでした。' };
  }
}
